import React, { useState, useEffect } from 'react';
import { Calendar, Cpu, Bot, Container, X, ZoomIn, Rocket, Sparkles } from 'lucide-react';
import { API_URL } from '../config';

const PastEvents = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [dynamicEvents, setDynamicEvents] = useState([]);

  // Sabit (gömülü) etkinlikler
  const staticEvents = [
    {
      id: 'static-1',
      title: 'IBM Teknik Lideri Gökçen Karasu ile Quantum Computing Konferansı',
      description: 'IBM Teknik Lideri Gökçen Karasu eşliğinde gerçekleştirdiğimiz kuantum hesaplama konferansında, geleceğin teknolojisi olan quantum computing\'in temellerini ve uygulama alanlarını detaylı şekilde ele aldık. Katılımcılarımız, kuantum bilgisayarların klasik bilgisayarlardan farkını ve bu teknolojinin endüstrideki potansiyelini öğrenme fırsatı buldular.',
      images: [
        { src: '/images/ibm1.jpg', alt: 'IBM Quantum Konferansı 1' },
        { src: '/images/ibm2.jpg', alt: 'IBM Quantum Konferansı 2' },
        { src: '/images/ibm3.jpg', alt: 'IBM Quantum Konferansı 3' }
      ],
      icon: 'Cpu',
      color: 'blue'
    },
    {
      id: 'static-2',
      title: 'Microsoft Teknik Lideri Daron Yöndem ile Yapay Zeka Konferansı',
      description: 'Microsoft Teknik Lideri Daron Yöndem ile gerçekleştirdiğimiz yapay zeka konferansında, makine öğrenimi, derin öğrenme ve yapay zeka uygulamalarının günümüzdeki kullanım alanlarını keşfettik. Etkinliğimizde, AI\'ın iş dünyasında ve günlük hayattaki devrimsel etkileri ele alındı ve katılımcılar yapay zeka teknolojilerinin geleceği hakkında değerli bilgiler edindiler.',
      images: [
        { src: '/images/daron1.jpg', alt: 'Microsoft AI Konferansı 1' },
        { src: '/images/daron2.jpg', alt: 'Microsoft AI Konferansı 2' }
      ],
      icon: 'Bot',
      color: 'purple'
    },
    {
      id: 'static-3',
      title: 'Dr. Öğr. Üyesi Fevzi Daş ile Docker Eğitimi',
      description: 'Dr. Öğretim Üyesi Fevzi Daş\'ın rehberliğinde gerçekleştirdiğimiz Docker eğitiminde, konteyner teknolojilerinin temellerini ve modern yazılım geliştirme süreçlerindeki önemini öğrendik. Uygulamalı eğitimimizde, Docker ile uygulama dağıtımı, mikroservis mimarileri ve DevOps pratikleri hakkında detaylı bilgiler edinildi.',
      images: [
        { src: '/images/d1.jpg', alt: 'Docker Eğitimi 1' },
        { src: '/images/d2.jpg', alt: 'Docker Eğitimi 2' },
        { src: '/images/d3.jpg', alt: 'Docker Eğitimi 3' }
      ],
      icon: 'Container',
      color: 'cyan'
    }
  ];

  // İkon map'i
  const iconMap = {
    Cpu, Bot, Container, Calendar
  };

  useEffect(() => {
    fetchDynamicEvents();
  }, []);

  const fetchDynamicEvents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/past-events`);
      if (response.ok) {
        const data = await response.json();
        setDynamicEvents(data);
      }
    } catch (error) {
      console.error('Dinamik etkinlikler alınamadı:', error);
    }
  };

  // Tüm etkinlikleri birleştir (dinamik + statik)
  const allEvents = [...dynamicEvents.map(event => ({
    ...event,
    images: event.images || []
  })), ...staticEvents];

  const starryBackground = {
    backgroundImage: `
      radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,0.8), transparent),
      radial-gradient(2px 2px at 60px 70px, rgba(255,255,255,0.6), transparent),
      radial-gradient(1px 1px at 50px 50px, rgba(255,255,255,0.4), transparent),
      radial-gradient(1px 1px at 130px 80px, rgba(255,255,255,0.5), transparent),
      radial-gradient(2px 2px at 90px 10px, rgba(255,255,255,0.7), transparent),
      radial-gradient(1.5px 1.5px at 180px 120px, rgba(255,255,255,0.6), transparent),
      radial-gradient(1px 1px at 200px 180px, rgba(255,255,255,0.4), transparent),
      radial-gradient(2px 2px at 250px 50px, rgba(255,255,255,0.8), transparent)
    `,
    backgroundSize: '200px 200px, 300px 300px, 400px 400px, 250px 250px, 350px 350px, 280px 280px, 320px 320px, 380px 380px',
    animation: 'starAnimation 60s linear infinite'
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
      purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
      cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30 text-cyan-400',
      green: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-400',
      orange: 'from-orange-500/20 to-orange-600/10 border-orange-500/30 text-orange-400',
      pink: 'from-pink-500/20 to-pink-600/10 border-pink-500/30 text-pink-400'
    };
    return colors[color] || colors.blue;
  };

  const getIconComponent = (iconName) => {
    return iconMap[iconName] || Calendar;
  };

  const openModal = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-950 to-slate-900 pt-28 pb-16 px-5 relative overflow-hidden"
      style={starryBackground}
    >
      {/* Dekoratif elementler */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-900/10 to-transparent pointer-events-none" />
      <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 right-10 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="p-3 bg-purple-500/20 rounded-2xl backdrop-blur-sm border border-purple-500/30">
              <Calendar className="w-8 h-8 text-purple-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
              Geçmiş Etkinliklerimiz
            </h1>
          </div>
          <p className="text-blue-200/60 text-lg max-w-2xl mx-auto">
            ACM Iğdır olarak düzenlediğimiz etkinlikler ve konferanslardan özel anlar
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 rounded-full border border-purple-500/20">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-purple-200/80 text-sm">{allEvents.length} etkinlik gerçekleştirildi</span>
          </div>
        </div>

        {/* Events */}
        <div className="space-y-12">
          {allEvents.map((event, index) => {
            const IconComponent = getIconComponent(event.icon);
            return (
              <div
                key={event.id}
                className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden transition-all duration-500 hover:border-white/20"
              >
                <div className="p-8 md:p-10">
                  {/* Event Header */}
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`p-3 rounded-2xl bg-gradient-to-br ${getColorClasses(event.color)} border`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 leading-tight group-hover:text-blue-300 transition-colors">
                        {event.title}
                      </h2>
                      <p className="text-blue-100/60 leading-relaxed">
                        {event.description}
                      </p>
                    </div>
                  </div>

                  {/* Image Gallery */}
                  {event.images && event.images.length > 0 && (
                    <div className={`grid gap-4 ${
                      event.images.length === 1 ? 'grid-cols-1' : 
                      event.images.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 
                      'grid-cols-1 md:grid-cols-3'
                    }`}>
                      {event.images.map((image, imgIndex) => (
                        <div
                          key={imgIndex}
                          className="relative group/image cursor-pointer overflow-hidden rounded-2xl bg-slate-800/50"
                          onClick={() => openModal(image)}
                        >
                          <div className="aspect-video overflow-hidden">
                            <img
                              src={image.src}
                              alt={image.alt}
                              className="w-full h-full object-cover transition-all duration-700 group-hover/image:scale-150"
                              loading="lazy"
                            />
                          </div>
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover/image:opacity-100 transition-all duration-300 flex items-center justify-center">
                            <div className="transform translate-y-4 group-hover/image:translate-y-0 transition-transform duration-300">
                              <div className="p-4 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
                                <ZoomIn className="w-8 h-8 text-white" />
                              </div>
                            </div>
                          </div>
                          {/* Bottom gradient text */}
                          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover/image:opacity-100 transition-opacity duration-300">
                            <p className="text-white text-sm font-medium">Büyütmek için tıklayın</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-full border border-white/10">
            <Rocket className="w-5 h-5 text-purple-400" />
            <span className="text-blue-200/80">Daha fazla etkinlik için bizi takip edin!</span>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4"
          onClick={closeModal}
          style={{ animation: 'fadeIn 0.3s ease-out' }}
        >
          <button
            className="absolute top-6 right-6 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-sm border border-white/20"
            onClick={closeModal}
            aria-label="Kapat"
          >
            <X className="w-6 h-6" />
          </button>

          <div
            className="relative max-w-5xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'slideInUp 0.4s ease-out' }}
          >
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="w-full h-full object-contain rounded-2xl shadow-2xl"
            />
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent rounded-b-2xl">
              <p className="text-white text-lg font-medium text-center">{selectedImage.alt}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PastEvents;
